import * as React from 'react';
import type { CSSProperties } from 'react';
import { cx } from '../utils';
import { PROGRESS_TINTS, type ProgressTint } from '../types';
import type { StyledProps } from '../types';
import type { UnsafeStyleProps } from '../escape-hatches';

/* Bounded rest-prop allow-list. Note: `aria-*` is NOT in the allow
   list because Progress has a controlled ARIA contract (valuenow /
   valuemin / valuemax / label). Allowing spread aria-* would let
   adopters silently overwrite the announcement contract. */
type AllowedRestKey = 'id' | 'title' | `data-${string}` | 'lang' | 'dir';
type AllowedRest = Partial<Record<AllowedRestKey, unknown>>;
const ALLOWED_LITERAL_KEYS = new Set<string>(['id', 'title', 'lang', 'dir']);
function pickAllowed(rest: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(rest)) {
    if (ALLOWED_LITERAL_KEYS.has(key) || key.startsWith('data-')) {
      out[key] = rest[key];
    }
  }
  return out;
}

export interface ProgressProps extends StyledProps, UnsafeStyleProps, AllowedRest {
  value: number;
  max?: number;
  tint?: ProgressTint;
  height?: number;
  showMarker?: boolean;
  /* Accessible name for the progressbar. Rendered as aria-label
     on the role="progressbar" root. Pass something like "Download
     progress"; screen readers will announce the percentage with
     this as context. Default: "Progress" for the bare case. */
  ariaLabel?: string;
}

export const Progress = React.forwardRef<HTMLDivElement, ProgressProps>(function Progress(
  {
    value,
    max = 1,
    tint = 'theme',
    height,
    showMarker,
    ariaLabel = 'Progress',
    className,
    unsafe_style,
    ...rest
  },
  ref,
) {
  const safeTint: ProgressTint = PROGRESS_TINTS.has(tint) ? tint : 'theme';
  /* Cap pct at 1.5 (not 1) so callers passing over-max values (e.g. a
     metric that blew past its target) render a visible "over" state
     via the data-overflow attribute below, rather than clamping
     silently and hiding the regression. Fill width is still clipped
     to 100%; the overflow indicator is a separate styling hook in
     kit.css. */
  const pct = Math.max(0, Math.min(1.5, value / max));
  const overflow = pct > 1;
  const widthPct = `${Math.min(pct, 1) * 100}%`;
  const heightStyle: CSSProperties | undefined = height ? { height: `${height}px` } : undefined;
  const mergedStyle: CSSProperties | undefined =
    heightStyle || unsafe_style ? { ...heightStyle, ...unsafe_style } : undefined;

  /* aria-valuenow must be within [aria-valuemin, aria-valuemax]
     per ARIA 1.2. Clamp the announced value to the advertised
     max — SRs never speak "120 of 100", they just announce the
     ceiling. The visual overflow signal is kept on data-overflow
     (below) so a consumer can still style the over-max state.
     Guard non-finite inputs (NaN / ±Infinity from divide-by-zero)
     separately: fall back to 0 / 1 to keep the attribute a valid
     number. */
  const ariaMax = Number.isFinite(max) && max > 0 ? max : 1;
  const ariaNow = Number.isFinite(value) ? Math.max(0, Math.min(ariaMax, value)) : 0;

  const restProps = pickAllowed(
    rest as Record<string, unknown>,
  ) as React.HTMLAttributes<HTMLDivElement>;

  return (
    <div
      ref={ref}
      {...restProps}
      className={cx('j-progress', `j-progress-${safeTint}`, className)}
      style={mergedStyle}
      role="progressbar"
      aria-valuenow={ariaNow}
      aria-valuemin={0}
      aria-valuemax={ariaMax}
      aria-label={ariaLabel}
    >
      <div
        className="j-progress-fill"
        style={{ width: widthPct }}
        data-overflow={overflow ? 'true' : undefined}
      />
      {showMarker && (
        <div
          className="j-progress-marker"
          style={{
            insetInlineStart: '100%',
            height: `calc(${height ? `${height}px` : 'var(--sp-sm)'} + var(--sp-xs))`,
          }}
        />
      )}
    </div>
  );
});
